import { mapMutations, mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { downloadAttachment } from '@/utils/vuex';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import pullAt from 'lodash/pullAt';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
const csvTitleCols = ['title', 'gwEui', 'fireflyGwId', 'originatorId', 'accountId', 'description', 'manufacturer', 'group', 'serialNumber', 'firmwareVersion', 'warningLevel', 'alertLevel', 'latitude', 'longitude', 'address'];
export default {
  name: 'NiotaClassicGwMgmtCsvImport',
  props: {
    accounts: {
      type: Array,
      required: false,
      default: () => []
    },
    isOpen: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      isBatchCreating: false,
      creatingIndex: -1,
      importValidForm: false,
      originators: [],
      gateways: [],
      prefilterError: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    creationProgress() {
      return Math.floor((this.creatingIndex + 1) / this.gateways.length * 100);
    },
    tableHeaders() {
      return [{
        sortable: false,
        text: this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.line'),
        value: 'idx'
      }, {
        sortable: false,
        text: this.$i18n_t('NiotaClassic.Common.table.title'),
        value: 'title'
      }, {
        sortable: false,
        text: this.$i18n_t('NiotaClassic.GWMonitoring.gwEui')
      }, {
        sortable: false,
        text: this.$i18n_t('NiotaClassic.GWMonitoring.form.fireflyGwId')
      }, {
        sortable: false,
        text: this.$i18n_t('NiotaClassic.Originators.title')
      }, {
        sortable: false,
        text: this.$i18n_t('NiotaClassic.GWMonitoring.account')
      }, {
        sortable: false,
        text: this.$i18n_t('NiotaClassic.GWMonitoring.form.manufacturer')
      }, {
        sortable: false,
        text: this.$i18n_t('Common.lat')
      }, {
        sortable: false,
        text: this.$i18n_t('Common.lon')
      }];
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [5, 10, 20]
      };
    },
    filteredRows() {
      return this.prefilterError ? this.gateways.filter(gw => gw.error) : this.gateways;
    }
  },
  created() {
    NiotaClassicApiService.originatorsFetchPlain().then(res => {
      this.originators = res.data.originators;
    }).catch(() => {
      this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingOriginators'), 'error');
    });
  },
  methods: {
    ...mapMutations('app', ['snackbar']),
    get,
    accountForId(accountId) {
      return (this.accounts.find(a => a.id === accountId) || {
        name: this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.accountUnknown')
      }).name;
    },
    originatorForId(originatorId) {
      return (this.originators.find(d => d.id === originatorId) || {
        title: this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.accountUnknown')
      }).title;
    },
    downloadSampleCsv() {
      const colSeparator = ',';
      const csvData = [`${csvTitleCols.join(colSeparator)}`, `${['Gateway name to create', '00000008004A718F', null, 9001, 1, 'Description without commas or linebreaks', 'DHARMA Initiative', 'Demo Group', 'A0001', '1-02-42a', 5, 12, 50.0987654321, 8.123456789, 'Storgata 4 | 9008 Tromsø | Norway'].join(colSeparator)}`, `${['Gateway name to import from Firefly', null, 12, 9001, 1, 'Description without commas or linebreaks', 'DHARMA Initiative', 'Demo Group', 'A0001', '1-02-42a', 5, 12, 50.0987654321, 8.123456789, 'Storgata 4 | 9008 Tromsø | Norway'].join(colSeparator)}`].join('\n');
      const fileName = 'create-n2-gateways.csv';
      downloadAttachment('text/plain;charset=utf-8', csvData, fileName);
    },
    async uploadedFile(csv) {
      let lines = csv.split('\n');
      if (lines.length <= 1) {
        // CSV only contains the header, nothing to do here
        // TODO: inform user (with snack?) about "invalid" CSV?
        return;
      }
      this.gateways = [];
      let colSeparator = ',';
      // let header = lines[0].trim().split(colSeparator);
      pullAt(lines, [0]);
      const headerIdxs = {};
      csvTitleCols.forEach((item, idx) => {
        headerIdxs[item] = idx;
      });
      lines.forEach((line, idx) => {
        const parts = line.trim().split(colSeparator);
        if (parts.length >= 6) {
          const gwData = {};
          gwData.idx = idx + 1;
          gwData.title = parts[headerIdxs['title']];
          const euiRegex = /^([0-9A-F]{16})$/gi;
          gwData.eui = euiRegex.exec(parts[headerIdxs['gwEui']].trim()) !== null ? parts[headerIdxs['gwEui']].trim() : null;
          gwData.fireflyGwId = gwData.eui ? null : parseInt(parts[headerIdxs['fireflyGwId']]) || null;
          gwData.accountId = parseInt(parts[headerIdxs['accountId']]);
          gwData.description = parts[headerIdxs['description']];
          gwData.properties = {
            originator: {
              id: parseInt(parts[headerIdxs['originatorId']])
            },
            manufacturer: parts[headerIdxs['manufacturer']],
            serialNr: parts[headerIdxs['serialNumber']],
            firmwareVersion: parts[headerIdxs['firmwareVersion']],
            group: parts[headerIdxs['group']],
            threshold: {
              green: get(parts, `[${headerIdxs['warningLevel']}]`),
              yellow: get(parts, `[${headerIdxs['alertLevel']}]`)
            },
            formatted_address: get(parts, `[${headerIdxs['address']}]`)
          };
          if (!gwData.title.trim()) {
            gwData.error = (gwData.error || []).concat([this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.errors.titleMissing')]);
          }
          if (!gwData.eui && (gwData.fireflyGwId === null || isNaN(gwData.fireflyGwId))) {
            gwData.error = (gwData.error || []).concat([this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.errors.euiMissing')]);
          }
          if (!gwData.accountId || !this.accounts.find(a => a.id === gwData.accountId)) {
            gwData.error = (gwData.error || []).concat([this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.errors.accountMissing')]);
          }
          if (!gwData.properties.originator.id || isNaN(gwData.properties.originator.id) || !this.originators.find(d => d.id === gwData.properties.originator.id)) {
            gwData.error = (gwData.error || []).concat([this.$i18n_t('NiotaClassic.GWMonitoring.csvCreate.errors.originatorMissing')]);
          }
          if (!isNaN(parseFloat(get(parts, `[${headerIdxs['latitude']}]`))) && !isNaN(parseFloat(get(parts, `[${headerIdxs['longitude']}]`)))) {
            gwData.coordinates = {
              coordinates: [parseFloat(parts[headerIdxs['longitude']]), parseFloat(parts[headerIdxs['latitude']])]
            };
          } else {
            gwData.coordinates = null;
          }
          this.gateways.push(gwData);
        }
      });
      this.gateways = cloneDeep(this.gateways);
    },
    printError(gateway) {
      if (get(gateway, 'error.message')) {
        const msgKey = `NiotaClassic.GWMonitoring.csvCreate.errors.${get(gateway, 'error.response.data.message')}`;
        return this.$i18n_t(msgKey) !== msgKey ? this.$i18n_t(msgKey) : `${get(gateway, 'error.message')} ${get(gateway, 'error.response.data.message')}`;
      }
      return gateway.error.join ? gateway.error.join(', ') : gateway.error;
    },
    resetGatewayError(index) {
      delete this.gateways[index].error;
      this.gateways = cloneDeep(this.gateways);
    },
    removeGateway(index) {
      pullAt(this.gateways, [index]);
      this.gateways = cloneDeep(this.gateways);
    },
    createGateways() {
      this.creatingIndex = 0;
      this.isBatchCreating = true;
      this.createSingleGatewayOrStop();
    },
    createSingleGatewayOrStop() {
      const index = this.creatingIndex;
      if (index >= this.gateways.length) {
        let i = 0;
        while (i < this.gateways.length) {
          if (this.gateways[i].id) {
            pullAt(this.gateways, [i]);
          } else {
            i++;
          }
        }
        this.gateways = cloneDeep(this.gateways);
        this.isBatchCreating = false;
        if (this.gateways.length === 0) {
          this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.createSuccess'), 'success');
          this.$emit('close');
        }
        return;
      }
      const gwData = this.gateways[index];
      ApiService.gwMgmtCreate(gwData).then(response => {
        gwData.id = response.id;
      }).catch(error => {
        gwData.error = error;
        this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.createFailed'), 'error');
        console.error(error);
      }).finally(() => {
        this.creatingIndex++;
        this.gateways = cloneDeep(this.gateways);
        this.createSingleGatewayOrStop();
      });
    }
  }
};